import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  InjectionToken,
  input,
  model,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, map, switchMap } from 'rxjs';

import { TolerantPostService } from '../../clients/tolerant-post/tolerant-post.service';
import { AddressField } from '../../customer/model/address-field.model';
import { CommunicationField } from '../../customer/model/communication-field.model';
import { CustomerAddress, CustomerContact } from '../../customer/model/customer-information.model';
import { Customer, makeBlankCustomer } from '../../customer/model/customer.model';
import { IdentityField } from '../../customer/model/identity-field.model';
import { CustomerValidationService } from '../../customer/services/customer-validation.service';
import {
  getMissingBusinessAddressFields,
  getMissingPrivateAddressFields,
} from '../../customer/services/mandatory-address';
import { useI18n } from '../../i18n/services/i18n.service';
import { deepEqual } from '../../utils/objectComparison';
import { normalizeProvinceCode } from '../../utils/province-code';
import { CustomerInformationDialogComponent } from './customer-information-dialog.component';

/**
 * This container is used to manage the lifecycle of the customer object which is used to store altered information about the customer.
 * As soon as the component is destroyed, which happens when the above modal is closed, the customer object is also destroyed.
 */
@Component({
  selector: 'cdf-customer-information-dialog-container',
  imports: [CustomerInformationDialogComponent],
  template: `
    <cdf-customer-information-dialog
      [(customer)]="modifiedCustomer"
      [invalidPrimaryAddressFields]="displayedInvalidPrimaryAddressFields()"
      [invalidBusinessAddressFields]="displayedInvalidBusinessAddressFields()"
      [invalidIdentityFields]="invalidIdentityFields()"
      [invalidPrimaryCommunicationFields]="invalidPrimaryCommunicationFields()"
      [invalidBusinessCommunicationFields]="invalidBusinessCommunicationFields()"
      [saving]="saving()"
      [saveButtonLabel]="saveButtonLabel() || i18n('button.saveChanges')"
      [editingIdentityDisabled]="editingIdentityDisabled()"
      [editingEmailDisabled]="editingEmailDisabled()"
      (closeModal)="closeModal.emit()"
      (saveChanges)="validateModifiedCustomerAndSave()"
      [primaryAddressValidationSkipped]="primaryAddressValidationSkipped()"
      [businessAddressValidationSkipped]="secondaryAddressValidationSkipped()"
      (skipPrimaryAddressValidation)="primaryAddressValidationSkipped.set(true)"
      (skipBusinessAddressValidation)="secondaryAddressValidationSkipped.set(true)"
    >
      <ng-content />
    </cdf-customer-information-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerInformationDialogContainer implements OnInit {
  static readonly validationDebounceSecondsInjectionToken = new InjectionToken<number>(
    'CustomerInformationDialogContainer.validationDebounceSecondsInjectionToken',
    { providedIn: 'root', factory: () => 1 },
  );

  private readonly validationDebounceSeconds = inject(
    CustomerInformationDialogContainer.validationDebounceSecondsInjectionToken,
  );

  readonly i18n = useI18n();

  readonly logger = inject(NGXLogger);
  readonly tolerantService = inject(TolerantPostService);
  readonly customerValidationService = inject(CustomerValidationService);

  readonly customer = input.required<Customer>();
  readonly saveButtonLabel = input<string>();
  readonly saving = model(false);

  // If true, all customer information is validated continuously, else only before saving.
  readonly continuouslyValidate = input(true);
  readonly editingIdentityDisabled = input(false);
  readonly editingEmailDisabled = input(false);

  readonly saveChanges = output<Customer>();
  readonly closeModal = output();

  readonly modifiedCustomer = signal<Customer>(makeBlankCustomer());

  // Validation
  readonly invalidIdentityFields = signal<IdentityField[]>([]);
  readonly invalidPrimaryCommunicationFields = signal<CommunicationField[]>([]);
  readonly invalidBusinessCommunicationFields = signal<CommunicationField[]>([]);

  // Continuous validation shall only be performed after the save button has been clicked when this.continuouslyValidate() === false
  readonly saveHasBeenClicked = signal(false);

  // Used to determine if identity or communication data has changed. If so, customer information might has to be re-validated.
  readonly modifiedCustomerHistory = signal<Customer | undefined>(makeBlankCustomer());

  readonly primaryAddressValidationSkipped = signal(false);
  readonly secondaryAddressValidationSkipped = signal(false);

  readonly invalidPrimaryAddressFields = this.createInvalidAddressFieldsSignal(
    (customer) => customer.privateContact.address,
    this.primaryAddressValidationSkipped,
  );

  readonly invalidBusinessAddressFields = this.createInvalidAddressFieldsSignal(
    (customer) => customer.businessContact.address,
    this.secondaryAddressValidationSkipped,
  );

  /**
   * Missing mandatory address fields (country, city, postal code).
   *
   * Deliberately independent of the Tolerant Post validation in getInvalidAddressFields: neither
   * "skip validation", an unchanged legacy address nor a Tolerant Post outage may let an incomplete
   * address through. Only shown after the first save attempt, so a blank creation form does not
   * start out red.
   */
  readonly missingPrivateAddressFields = computed(() =>
    this.saveHasBeenClicked() ? getMissingPrivateAddressFields(this.modifiedCustomer().privateContact.address) : [],
  );

  readonly missingBusinessAddressFields = computed(() =>
    this.saveHasBeenClicked() ? getMissingBusinessAddressFields(this.modifiedCustomer().businessContact.address) : [],
  );

  readonly displayedInvalidPrimaryAddressFields = computed(() => [
    ...new Set([...this.invalidPrimaryAddressFields(), ...this.missingPrivateAddressFields()]),
  ]);

  readonly displayedInvalidBusinessAddressFields = computed(() => [
    ...new Set([...this.invalidBusinessAddressFields(), ...this.missingBusinessAddressFields()]),
  ]);

  // Used to determine invalid address fields regularily on change.
  // However, not used to determine invalidity right before saving. "getInvalidAddressFields" is used directly instead.
  private createInvalidAddressFieldsSignal(
    addressSelector: (customer: Customer) => CustomerAddress | undefined,
    isValidationSkipped: WritableSignal<boolean>,
  ) {
    const address = toObservable(this.modifiedCustomer).pipe(
      filter((customer) => Boolean(customer)),
      map((customer) => addressSelector(customer)),
      distinctUntilChanged(),
      debounceTime(this.validationDebounceSeconds * 1000),
    );

    return toSignal(
      combineLatest([
        address,
        toObservable(this.saveHasBeenClicked), // Trigger recomputation if save has been clicked
        toObservable(isValidationSkipped), // Trigger recomputation if skip has been clicked
      ]).pipe(
        // Only compute validation if continuous validation is active, or save has been clicked
        filter(([_address, saveClicked, _skipped]) => this.continuouslyValidate() || saveClicked),
        switchMap(([address, _saveClicked, skipped]) =>
          this.getInvalidAddressFields(address, addressSelector(this.customer()), skipped),
        ),
        catchError((e) => {
          this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', e);
          return EMPTY;
        }),
      ),
      { initialValue: [] },
    );
  }

  readonly syncOnInputChange = effect(() => {
    const customer = this.customer();
    if (customer) {
      this.modifiedCustomer.set(customer);
    }
  });

  readonly validateInformationOnChange = effect(() => {
    if (this.continuouslyValidate() || this.saveHasBeenClicked()) {
      if (this.hasIdentityInformationChanged()) {
        this.invalidIdentityFields.set(
          this.customerValidationService.validateIdentityInformation(
            this.modifiedCustomer(),
            this.editingIdentityDisabled(),
          ),
        );
      }
      if (this.hasCommunicationInformationChanged()) {
        this.validateCommunicationInformation();
      }
      this.modifiedCustomerHistory.set(this.modifiedCustomer());
    }
  });

  ngOnInit() {
    // Set values of the customer creation form to values entered when searching for a customer
    this.modifiedCustomer.set(this.customer());
  }

  async validateModifiedCustomerAndSave() {
    this.saving.set(true);

    // Marks the first save attempt. Besides enabling validation when continuouslyValidate() is false,
    // it switches on the display of missing mandatory address fields.
    this.saveHasBeenClicked.set(true);

    // If no continuous validation is enabled, validation has to be triggered manually
    if (!this.continuouslyValidate()) {
      // The following methods set signals:
      // - invalidPrimaryCommunicationFields
      // - invalidBusinessCommunicationFields
      // - invalidIdentityFields
      this.validateIdentityInformation();
      this.validateCommunicationInformation();
    }

    // Mandatory address fields - checked synchronously and independent of Tolerant Post
    const missingPrivateAddressFields = getMissingPrivateAddressFields(this.modifiedCustomer().privateContact.address);
    const missingBusinessAddressFields = getMissingBusinessAddressFields(
      this.modifiedCustomer().businessContact.address,
    );

    // Start the validation requests
    const privateValidation = this.getInvalidAddressFields(
      this.modifiedCustomer().privateContact.address,
      this.customer().privateContact.address,
      this.primaryAddressValidationSkipped(),
    );
    const businessValidation = this.getInvalidAddressFields(
      this.modifiedCustomer().businessContact.address,
      this.customer().businessContact.address,
      this.secondaryAddressValidationSkipped(),
    );

    if (
      missingPrivateAddressFields.length === 0 &&
      missingBusinessAddressFields.length === 0 &&
      // wait for the responses of the validation requests
      (await privateValidation).length === 0 &&
      (await businessValidation).length === 0 &&
      this.invalidPrimaryCommunicationFields().length === 0 &&
      this.invalidBusinessCommunicationFields().length === 0 &&
      this.invalidIdentityFields().length === 0
    ) {
      // Set provinces after address information is found to be valid.
      // This has to be done, as the state is not actively set by the user.
      try {
        await this.setProvinces();
      } catch {
        this.logger.warn('#CDFTF0030 Failed to set provinces for customer.');
      }

      // Remove spaces from taxNumber
      const normalizedCustomer: Customer = {
        ...this.modifiedCustomer(),
        taxNumber: this.modifiedCustomer().taxNumber?.replace(/\s+/g, ''),
        businessContact: this.normalizeCustomerContact(this.modifiedCustomer().businessContact),
        privateContact: this.normalizeCustomerContact(this.modifiedCustomer().privateContact),
      };

      this.saveChanges.emit(normalizedCustomer);

      // Intentionally did not set "saving" to "false" after successfully emitting "saveChanges" as the progress of saving is continued by emitting "saveChanges".
      // Setting "saving" to "false", is the responsibility of one of the parent components
    } else {
      // If invalid fields were found, disable saving state
      this.saving.set(false);
    }
  }

  normalizeCustomerContact(contact: CustomerContact): CustomerContact {
    return {
      ...contact,
      phone: this.customerValidationService.normalizePhoneNumber(contact.phone),
      mobile: this.customerValidationService.normalizePhoneNumber(contact.mobile),
    };
  }

  validateIdentityInformation() {
    this.invalidIdentityFields.set(
      this.customerValidationService.validateIdentityInformation(
        this.modifiedCustomer(),
        this.editingIdentityDisabled(),
      ),
    );
  }

  validateCommunicationInformation() {
    // Validate private information
    this.invalidPrimaryCommunicationFields.set(
      this.customerValidationService.validateCommunicationInformation(
        this.modifiedCustomer().privateContact.phone,
        this.modifiedCustomer().privateContact.mobile,
        this.modifiedCustomer().privateContact.email,
        this.modifiedCustomer().privateContact.address?.country,
      ),
    );

    // Validate business information
    this.invalidBusinessCommunicationFields.set(
      this.customerValidationService.validateCommunicationInformation(
        this.modifiedCustomer().businessContact.phone,
        this.modifiedCustomer().businessContact.mobile,
        this.modifiedCustomer().businessContact.email,
        this.modifiedCustomer().businessContact.address?.country,
        false,
      ),
    );
  }

  async getInvalidAddressFields(
    modifiedAddress: CustomerAddress | undefined,
    originalAddress: CustomerAddress | undefined,
    skipValidation: boolean,
  ): Promise<AddressField[]> {
    if (
      !modifiedAddress ||
      !this.customerValidationService.isRelevantAddressInformationSet(modifiedAddress) ||
      skipValidation
    ) {
      // It is allowed to leave the address unset. Then all fields are valid.
      return [];
    }

    const addressUnchanged = deepEqual(modifiedAddress, originalAddress);
    if (addressUnchanged) {
      return [];
    }

    try {
      const suggestions = await this.tolerantService.resolveAddressInteractive(modifiedAddress);
      const invalidFields = this.customerValidationService.getInvalidFields(modifiedAddress, suggestions);
      const missingFields = this.getMissingButRequiredAddressInformation(modifiedAddress);
      return [...new Set([...invalidFields, ...missingFields])];
    } catch (e) {
      this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', e);
      return [];
    }
  }

  getMissingButRequiredAddressInformation(address: CustomerAddress): AddressField[] {
    const missingAddressFields = [];
    if (!address.postalCode) missingAddressFields.push(AddressField.POSTAL_CODE);
    if (!address.city) missingAddressFields.push(AddressField.CITY);
    if (!address.street) missingAddressFields.push(AddressField.STREET);
    if (!address.country) missingAddressFields.push(AddressField.COUNTRY);
    return missingAddressFields;
  }

  async setProvinces() {
    const primaryAddress = this.modifiedCustomer().privateContact.address;
    const businessAddress = this.modifiedCustomer().businessContact.address;

    if (primaryAddress && this.customerValidationService.isRelevantAddressInformationSet(primaryAddress)) {
      const primaryAddressValidationData = await this.tolerantService
        .resolveAddressInteractive(primaryAddress)
        .catch((error) => {
          this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', error);
          return [];
        });
      this.setProvince(
        'privateContact',
        this.deriveProvince(
          primaryAddressValidationData[0]?.provinceCode,
          primaryAddress,
          this.customer().privateContact.address,
        ),
      );
    }

    if (businessAddress && this.customerValidationService.isRelevantAddressInformationSet(businessAddress)) {
      const businessAddressValidationData = await this.tolerantService
        .resolveAddressInteractive(businessAddress)
        .catch((error) => {
          this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', error);
          return []; // Empty list leads to invalidity of address
        });
      this.setProvince(
        'businessContact',
        this.deriveProvince(
          businessAddressValidationData[0]?.provinceCode,
          businessAddress,
          this.customer().businessContact.address,
        ),
      );
    }
  }

  /**
   * Determines the province to store for an address.
   * A genuinely derived province from Tolerant Post always wins. Tolerant Post returns the COUNTRY code as
   * province whenever it cannot derive a real one (e.g. Ireland -> "IE", Luxembourg -> "LU"), which is no
   * province information at all. In that case - and when Tolerant Post returns nothing or fails - the stored
   * province is kept only if the address is unchanged; otherwise it is cleared, because a stale region would
   * corrupt the new address.
   */
  private deriveProvince(
    raw: string | undefined,
    address: CustomerAddress,
    original: CustomerAddress | undefined,
  ): string | undefined {
    const country = address.country?.toUpperCase();
    const rawUpper = raw?.toUpperCase();
    const derived = rawUpper && rawUpper !== country ? normalizeProvinceCode(raw, address.country) : undefined;
    if (derived) return derived;

    return isSameLocation(address, original) ? address.province : '';
  }

  setProvince(contactType: 'businessContact' | 'privateContact', province: string | undefined) {
    if (contactType === 'privateContact') {
      this.modifiedCustomer.update((customer) => ({
        ...customer,
        privateContact: {
          ...customer.privateContact,
          address: {
            ...customer.privateContact.address,
            province,
          },
        },
      }));
    } else {
      this.modifiedCustomer.update((customer) => ({
        ...customer,
        businessContact: {
          ...customer.businessContact,
          address: {
            ...customer.businessContact.address,
            province,
          },
        },
      }));
    }
  }

  /**
   * All identity fields to check for modification.
   *
   * This is a record so that the typechecker can ensure that all fields have been considered.
   */
  private static readonly allIdentityFields: Record<IdentityField, (keyof Customer)[]> = {
    BMW_MINI_ID: ['bmwMiniId'],
    CORRESPONDENCE_LANGUAGE: ['correspondenceLanguage'],
    DMS_ID: [], // DMS IDs are not modifiable
    FIRST_NAME: ['firstName'],
    LAST_NAME: ['lastName'],
    MIDDLE_NAME: ['middleName'],
    NATIONAL_ID: ['nationalId1'],
    NATIONAL_ID_DOCUMENT_TYPE: ['nationalId1DocumentType'],
    SALUTATION: ['salutation'],
    SUFFIX: ['suffix'],
    TAX_NUMBER: ['taxNumber', 'taxNumberCountry'],
    TITLE: ['title'],
  };

  hasIdentityInformationChanged(): boolean {
    const modifiedCustomer = this.modifiedCustomer();
    const previousCustomer = this.modifiedCustomerHistory();
    return Object.values(CustomerInformationDialogContainer.allIdentityFields).some((keys) =>
      keys.some((key) => modifiedCustomer[key] !== previousCustomer?.[key]),
    );
  }

  hasCommunicationInformationChanged(): boolean {
    const modifiedCustomerHistory = this.modifiedCustomerHistory();
    if (!modifiedCustomerHistory) {
      return true;
    }

    // Check if any contact field other than the address information has changed
    const { address: businessAddress, ...businessCommunicationInformation } = this.modifiedCustomer().businessContact;
    const { address: privateAddress, ...privateCommunicationInformation } = this.modifiedCustomer().privateContact;
    const { address: businessAddressHistory, ...businessCommunicationInformationHistory } =
      modifiedCustomerHistory.businessContact;
    const { address: privateAddressHistory, ...privateCommunicationInformationHistory } =
      modifiedCustomerHistory.privateContact;

    return (
      !deepEqual(businessCommunicationInformation, businessCommunicationInformationHistory) ||
      !deepEqual(privateCommunicationInformation, privateCommunicationInformationHistory)
    );
  }
}

/** The address fields that locate an address; the province is excluded because it is derived from them. */
const LOCATION_FIELDS = [
  'street',
  'houseNumber',
  'postalCode',
  'city',
  'country',
  'addressLine1',
  'addressLine2',
  'postBox',
] as const satisfies readonly (keyof CustomerAddress)[];

const normalized = (value: unknown): string => (typeof value === 'string' ? value.trim().toUpperCase() : '');

/**
 * Compares two addresses field by field, treating missing, undefined and blank values alike - the dialog
 * adds keys like addressLine2: undefined, which must not count as a change.
 */
function isSameLocation(address: CustomerAddress, original: CustomerAddress | undefined): boolean {
  return LOCATION_FIELDS.every((field) => normalized(address[field]) === normalized(original?.[field]));
}
